import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Shield,
  TrendingUp,
  Users,
  Plus,
  RefreshCw,
  Eye,
  Edit,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  PageHeader,
  MetricCard,
  KPIGrid,
  DataTable,
  Column,
  TableFilters,
} from '@/components/common';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import {
  riskManagementApi,
  RiskProfile,
  RiskDashboard,
  CreateRiskProfileDto,
  UpdateRiskProfileDto,
} from '@/api/services/risk-management.api';
import { inventoryApi, InventoryItem } from '@/api/services/inventory.api';
import { formatDate } from '@/lib/utils';
import { getRiskBadgeVariant, getRiskLabel, getRiskColor } from '@/lib/risk';

export default function RiskManagement() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<RiskDashboard | null>(null);
  const [profiles, setProfiles] = useState<RiskProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [riskFilter, setRiskFilter] = useState<string>('all');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<RiskProfile | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateRiskProfileDto>({
    itemId: '',
    supplyRisk: 5,
    demandRisk: 5,
    qualityRisk: 5,
    priceRisk: 5,
    obsolescenceRisk: 5,
    singleSourced: false,
    longLeadTime: false,
    highValue: false,
    criticalItem: false,
    volatilePricing: false,
    safetyStockDays: 30,
  });

  // Items for selection
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [dashboardRes, profilesRes] = await Promise.all([
        riskManagementApi.getDashboard(),
        riskManagementApi.getAll({
          page: pagination.page,
          limit: pagination.limit,
          search: searchQuery || undefined,
          minRisk: riskFilter === 'high' ? 7 : riskFilter === 'medium' ? 4 : undefined,
          maxRisk: riskFilter === 'low' ? 3 : riskFilter === 'medium' ? 6 : undefined,
        }),
      ]);

      setDashboard(dashboardRes.data);
      setProfiles(profilesRes.data.data);
      setPagination((prev) => ({
        ...prev,
        total: profilesRes.data.pagination.total,
        totalPages: profilesRes.data.pagination.totalPages,
      }));
    } catch (error) {
      toast.error('Failed to load risk data');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, searchQuery, riskFilter, pagination.limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (showCreateModal) {
      fetchInventoryItems();
    }
  }, [showCreateModal]);

  const fetchInventoryItems = async () => {
    try {
      setLoadingItems(true);
      const response = await inventoryApi.getAll({ limit: 100 });
      setInventoryItems(response.data);
    } catch (error) {
      toast.error('Failed to load inventory items');
    } finally {
      setLoadingItems(false);
    }
  };

  const handleCreate = async () => {
    try {
      if (!formData.itemId) {
        toast.error('Please select an inventory item');
        return;
      }

      await riskManagementApi.create(formData);
      toast.success('Risk profile created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create risk profile');
    }
  };

  const handleUpdate = async () => {
    if (!selectedProfile) return;

    try {
      const updateData: UpdateRiskProfileDto = {
        supplyRisk: formData.supplyRisk,
        demandRisk: formData.demandRisk,
        qualityRisk: formData.qualityRisk,
        priceRisk: formData.priceRisk,
        obsolescenceRisk: formData.obsolescenceRisk,
        singleSourced: formData.singleSourced,
        longLeadTime: formData.longLeadTime,
        highValue: formData.highValue,
        criticalItem: formData.criticalItem,
        volatilePricing: formData.volatilePricing,
        mitigationStrategy: formData.mitigationStrategy,
        safetyStockDays: formData.safetyStockDays,
        notes: formData.notes,
      };

      await riskManagementApi.update(selectedProfile.id, updateData);
      toast.success('Risk profile updated successfully');
      setShowEditModal(false);
      setSelectedProfile(null);
      resetForm();
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update risk profile');
    }
  };

  const handleRunAssessment = async (profile: RiskProfile) => {
    try {
      await riskManagementApi.runAssessment(profile.id);
      toast.success('Risk assessment completed');
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to run assessment');
    }
  };

  const handleDelete = async (profile: RiskProfile) => {
    if (!confirm(`Are you sure you want to delete the risk profile for "${profile.item?.name}"?`)) {
      return;
    }

    try {
      await riskManagementApi.delete(profile.id);
      toast.success('Risk profile deleted successfully');
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete risk profile');
    }
  };

  const openEditModal = (profile: RiskProfile) => {
    setSelectedProfile(profile);
    setFormData({
      itemId: profile.itemId,
      supplyRisk: profile.supplyRisk,
      demandRisk: profile.demandRisk,
      qualityRisk: profile.qualityRisk,
      priceRisk: profile.priceRisk,
      obsolescenceRisk: profile.obsolescenceRisk,
      singleSourced: profile.singleSourced,
      longLeadTime: profile.longLeadTime,
      highValue: profile.highValue,
      criticalItem: profile.criticalItem,
      volatilePricing: profile.volatilePricing,
      mitigationStrategy: profile.mitigationStrategy,
      safetyStockDays: profile.safetyStockDays,
      notes: profile.notes,
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      itemId: '',
      supplyRisk: 5,
      demandRisk: 5,
      qualityRisk: 5,
      priceRisk: 5,
      obsolescenceRisk: 5,
      singleSourced: false,
      longLeadTime: false,
      highValue: false,
      criticalItem: false,
      volatilePricing: false,
      safetyStockDays: 30,
    });
  };

  if (loading && !dashboard) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const riskProfileColumns: Column<RiskProfile>[] = [
    {
      key: 'item.name',
      linkTo: (profile) => `/risk/${profile.id}`,
      header: 'Item',
      sortable: true,
      render: (profile) => <span className="font-medium">{profile.item?.name || 'Unknown'}</span>,
    },
    {
      key: 'item.sku',
      header: 'SKU',
      render: (profile) => <span className="text-muted-foreground">{profile.item?.sku}</span>,
    },
    {
      key: 'item.category',
      header: 'Category',
      render: (profile) => <>{profile.item?.category || '-'}</>,
    },
    {
      key: 'overallRisk',
      header: 'Overall Risk',
      sortable: true,
      className: 'text-center',
      render: (profile) => (
        <Badge variant={getRiskBadgeVariant(profile.overallRisk)}>
          {profile.overallRisk}/10 - {getRiskLabel(profile.overallRisk)}
        </Badge>
      ),
    },
    {
      key: 'supplyRisk',
      header: 'Supply',
      sortable: true,
      className: 'text-center',
      render: (profile) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${getRiskColor(profile.supplyRisk)}`}
        >
          {profile.supplyRisk}
        </span>
      ),
    },
    {
      key: 'demandRisk',
      header: 'Demand',
      sortable: true,
      className: 'text-center',
      render: (profile) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${getRiskColor(profile.demandRisk)}`}
        >
          {profile.demandRisk}
        </span>
      ),
    },
    {
      key: 'riskFactors',
      header: 'Risk Factors',
      render: (profile) => (
        <div className="flex flex-wrap gap-1">
          {profile.singleSourced && (
            <Badge variant="outline" className="text-xs">
              Single Source
            </Badge>
          )}
          {profile.criticalItem && (
            <Badge variant="outline" className="text-xs">
              Critical
            </Badge>
          )}
          {profile.longLeadTime && (
            <Badge variant="outline" className="text-xs">
              Long Lead
            </Badge>
          )}
          {profile.highValue && (
            <Badge variant="outline" className="text-xs">
              High Value
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'lastAssessedAt',
      header: 'Last Assessed',
      sortable: true,
      render: (profile) => (
        <span className="text-muted-foreground">
          {profile.lastAssessedAt ? formatDate(profile.lastAssessedAt) : '-'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-right',
      render: (profile) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/risk/${profile.id}`);
            }}
            title="View Details"
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              handleRunAssessment(profile);
            }}
            title="Run Assessment"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              openEditModal(profile);
            }}
            title="Edit"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(profile);
            }}
            title="Delete"
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Supply Risk"
          description="Supply and demand exposure by stock item, with the factors behind each assessment."
          actions={
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Risk Profile
            </Button>
          }
        />
      </motion.div>

      {/* KPI Cards */}
      <motion.div variants={fadeUp}>
        <KPIGrid columns={4}>
          <MetricCard
            label="High Risk Items"
            value={dashboard?.highRiskCount || 0}
            subtitle="Risk score >= 7"
            sentiment={(dashboard?.highRiskCount || 0) > 0 ? 'danger' : 'success'}
            icon={<AlertTriangle className="h-5 w-5" />}
          />
          <MetricCard
            label="Single-Sourced"
            value={dashboard?.singleSourcedCount || 0}
            subtitle="Items with one supplier"
            sentiment={(dashboard?.singleSourcedCount || 0) > 0 ? 'warning' : 'success'}
            icon={<Users className="h-5 w-5" />}
          />
          <MetricCard
            label="Critical Items"
            value={dashboard?.criticalItemCount || 0}
            subtitle="Business-critical inventory"
            sentiment="info"
            icon={<Shield className="h-5 w-5" />}
          />
          <MetricCard
            label="Long Lead Time"
            value={dashboard?.longLeadTimeCount || 0}
            subtitle="Extended procurement time"
            sentiment="info"
            icon={<TrendingUp className="h-5 w-5" />}
          />
        </KPIGrid>

        {/* Risk Distribution */}
        {dashboard?.riskDistribution && (
          <Card>
            <CardHeader>
              <h3 className="text-base font-medium text-foreground">Risk Distribution</h3>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex h-4 rounded-full overflow-hidden">
                    {dashboard.riskDistribution.low > 0 && (
                      <div
                        className="bg-green-500"
                        style={{
                          width: `${(dashboard.riskDistribution.low / dashboard.totalProfiles) * 100}%`,
                        }}
                      />
                    )}
                    {dashboard.riskDistribution.medium > 0 && (
                      <div
                        className="bg-yellow-500"
                        style={{
                          width: `${(dashboard.riskDistribution.medium / dashboard.totalProfiles) * 100}%`,
                        }}
                      />
                    )}
                    {dashboard.riskDistribution.high > 0 && (
                      <div
                        className="bg-orange-500"
                        style={{
                          width: `${(dashboard.riskDistribution.high / dashboard.totalProfiles) * 100}%`,
                        }}
                      />
                    )}
                    {dashboard.riskDistribution.critical > 0 && (
                      <div
                        className="bg-red-500"
                        style={{
                          width: `${(dashboard.riskDistribution.critical / dashboard.totalProfiles) * 100}%`,
                        }}
                      />
                    )}
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded bg-green-500" />
                      Low ({dashboard.riskDistribution.low})
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded bg-yellow-500" />
                      Medium ({dashboard.riskDistribution.medium})
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded bg-orange-500" />
                      High ({dashboard.riskDistribution.high})
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded bg-red-500" />
                      Critical ({dashboard.riskDistribution.critical})
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Risk Profiles Table */}
        <DataTable<RiskProfile>
          toolbar={
            <TableFilters
              search={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder="Search items..."
              filters={[
                {
                  id: 'risk',
                  label: 'Risk Levels',
                  value: riskFilter,
                  options: [
                    { value: 'low', label: 'Low (1-3)' },
                    { value: 'medium', label: 'Medium (4-6)' },
                    { value: 'high', label: 'High (7-10)' },
                  ],
                  onChange: setRiskFilter,
                  placeholder: 'Filter by risk',
                  width: 'w-[180px]',
                },
              ]}
            />
          }
          onRowClick={(profile) => navigate(`/risk/${profile.id}`)}
          data={profiles}
          columns={riskProfileColumns}
          loading={loading}
          emptyTitle="No risk profiles found"
          emptyDescription="Create your first risk profile to get started."
          pagination
          exportable
          columnToggle
        />

        {/* Create Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Risk Profile</DialogTitle>
              <DialogDescription>Add a new risk profile for an inventory item</DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Item Selection */}
              <div className="space-y-2">
                <Label>Inventory Item *</Label>
                <Select
                  value={formData.itemId}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, itemId: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={loadingItems ? 'Loading...' : 'Select item'} />
                  </SelectTrigger>
                  <SelectContent>
                    {inventoryItems.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name} ({item.sku})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Risk Scores */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Supply Risk: {formData.supplyRisk}</Label>
                  <Slider
                    value={[formData.supplyRisk || 5]}
                    min={1}
                    max={10}
                    step={1}
                    onValueChange={([value]) =>
                      setFormData((prev) => ({ ...prev, supplyRisk: value }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Demand Risk: {formData.demandRisk}</Label>
                  <Slider
                    value={[formData.demandRisk || 5]}
                    min={1}
                    max={10}
                    step={1}
                    onValueChange={([value]) =>
                      setFormData((prev) => ({ ...prev, demandRisk: value }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Quality Risk: {formData.qualityRisk}</Label>
                  <Slider
                    value={[formData.qualityRisk || 5]}
                    min={1}
                    max={10}
                    step={1}
                    onValueChange={([value]) =>
                      setFormData((prev) => ({ ...prev, qualityRisk: value }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Price Risk: {formData.priceRisk}</Label>
                  <Slider
                    value={[formData.priceRisk || 5]}
                    min={1}
                    max={10}
                    step={1}
                    onValueChange={([value]) =>
                      setFormData((prev) => ({ ...prev, priceRisk: value }))
                    }
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Obsolescence Risk: {formData.obsolescenceRisk}</Label>
                  <Slider
                    value={[formData.obsolescenceRisk || 5]}
                    min={1}
                    max={10}
                    step={1}
                    onValueChange={([value]) =>
                      setFormData((prev) => ({ ...prev, obsolescenceRisk: value }))
                    }
                  />
                </div>
              </div>

              {/* Risk Factors */}
              <div className="space-y-4">
                <Label className="text-base">Risk Factors</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="singleSourced">Single Sourced</Label>
                    <Switch
                      id="singleSourced"
                      checked={formData.singleSourced}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({ ...prev, singleSourced: checked }))
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="criticalItem">Critical Item</Label>
                    <Switch
                      id="criticalItem"
                      checked={formData.criticalItem}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({ ...prev, criticalItem: checked }))
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="longLeadTime">Long Lead Time</Label>
                    <Switch
                      id="longLeadTime"
                      checked={formData.longLeadTime}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({ ...prev, longLeadTime: checked }))
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="highValue">High Value</Label>
                    <Switch
                      id="highValue"
                      checked={formData.highValue}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({ ...prev, highValue: checked }))
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="volatilePricing">Volatile Pricing</Label>
                    <Switch
                      id="volatilePricing"
                      checked={formData.volatilePricing}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({ ...prev, volatilePricing: checked }))
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Safety Stock */}
              <div className="space-y-2">
                <Label>Safety Stock Days: {formData.safetyStockDays}</Label>
                <Slider
                  value={[formData.safetyStockDays || 30]}
                  min={0}
                  max={180}
                  step={5}
                  onValueChange={([value]) =>
                    setFormData((prev) => ({ ...prev, safetyStockDays: value }))
                  }
                />
              </div>

              {/* Mitigation & Notes */}
              <div className="space-y-2">
                <Label>Mitigation Strategy</Label>
                <Textarea
                  placeholder="Describe mitigation strategies for this item..."
                  value={formData.mitigationStrategy || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, mitigationStrategy: e.target.value }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  placeholder="Additional notes..."
                  value={formData.notes || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate}>Create Profile</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Risk Profile</DialogTitle>
              <DialogDescription>
                Update risk assessment for {selectedProfile?.item?.name}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Risk Scores */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Supply Risk: {formData.supplyRisk}</Label>
                  <Slider
                    value={[formData.supplyRisk || 5]}
                    min={1}
                    max={10}
                    step={1}
                    onValueChange={([value]) =>
                      setFormData((prev) => ({ ...prev, supplyRisk: value }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Demand Risk: {formData.demandRisk}</Label>
                  <Slider
                    value={[formData.demandRisk || 5]}
                    min={1}
                    max={10}
                    step={1}
                    onValueChange={([value]) =>
                      setFormData((prev) => ({ ...prev, demandRisk: value }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Quality Risk: {formData.qualityRisk}</Label>
                  <Slider
                    value={[formData.qualityRisk || 5]}
                    min={1}
                    max={10}
                    step={1}
                    onValueChange={([value]) =>
                      setFormData((prev) => ({ ...prev, qualityRisk: value }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Price Risk: {formData.priceRisk}</Label>
                  <Slider
                    value={[formData.priceRisk || 5]}
                    min={1}
                    max={10}
                    step={1}
                    onValueChange={([value]) =>
                      setFormData((prev) => ({ ...prev, priceRisk: value }))
                    }
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Obsolescence Risk: {formData.obsolescenceRisk}</Label>
                  <Slider
                    value={[formData.obsolescenceRisk || 5]}
                    min={1}
                    max={10}
                    step={1}
                    onValueChange={([value]) =>
                      setFormData((prev) => ({ ...prev, obsolescenceRisk: value }))
                    }
                  />
                </div>
              </div>

              {/* Risk Factors */}
              <div className="space-y-4">
                <Label className="text-base">Risk Factors</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="edit-singleSourced">Single Sourced</Label>
                    <Switch
                      id="edit-singleSourced"
                      checked={formData.singleSourced}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({ ...prev, singleSourced: checked }))
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="edit-criticalItem">Critical Item</Label>
                    <Switch
                      id="edit-criticalItem"
                      checked={formData.criticalItem}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({ ...prev, criticalItem: checked }))
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="edit-longLeadTime">Long Lead Time</Label>
                    <Switch
                      id="edit-longLeadTime"
                      checked={formData.longLeadTime}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({ ...prev, longLeadTime: checked }))
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="edit-highValue">High Value</Label>
                    <Switch
                      id="edit-highValue"
                      checked={formData.highValue}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({ ...prev, highValue: checked }))
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="edit-volatilePricing">Volatile Pricing</Label>
                    <Switch
                      id="edit-volatilePricing"
                      checked={formData.volatilePricing}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({ ...prev, volatilePricing: checked }))
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Safety Stock */}
              <div className="space-y-2">
                <Label>Safety Stock Days: {formData.safetyStockDays}</Label>
                <Slider
                  value={[formData.safetyStockDays || 30]}
                  min={0}
                  max={180}
                  step={5}
                  onValueChange={([value]) =>
                    setFormData((prev) => ({ ...prev, safetyStockDays: value }))
                  }
                />
              </div>

              {/* Mitigation & Notes */}
              <div className="space-y-2">
                <Label>Mitigation Strategy</Label>
                <Textarea
                  placeholder="Describe mitigation strategies for this item..."
                  value={formData.mitigationStrategy || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, mitigationStrategy: e.target.value }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  placeholder="Additional notes..."
                  value={formData.notes || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdate}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Modal */}
        {/* A record is inspected here, not acted on, so this is a sheet
            rather than a modal. A dialog blocks the page behind it, which is
            right for a decision and wrong for reading. */}
        {/* The detail sheet lived here. It is a page now, at /risk/:id, so
            an assessment can be linked to and returned to. */}
      </motion.div>
    </motion.main>
  );
}
